/** Feature-facing hooks; no implementation details cross the frozen service boundary. */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useConversation, useTimeline } from '@/model/hooks';
import { useServices } from '@/model/context';
import { useLive } from '@/model/live';
import { audioFocusFor, type AudioFocusService } from './audio-focus';
import { unavailablePushState, webPushControllerLive, type WebPushController, type WebPushState } from './push-host';
import { webVersion } from './protocol';
import type { Account, Id } from '@/model';
import { acknowledgedCapabilities } from './capabilities';
import { decodeSupportUpdate, type SupportUpdate } from './support';
import { documentJournalFor, documentResourcesFor, imageGalleryFor } from './document-host';
import type { DocumentHostIdentity } from './document-resources';
export * from '@/model/hooks';
export { useServices } from '@/model/context';
export { useLive } from '@/model/live';

/** A stable combined Live for profiles needed by a visible list, including late account hydration. */
export function useAccountProfiles(ids: readonly Id[]): readonly Account[] {
  const services = useServices(); const key = [...new Set(ids)].sort().join(',');
  const profiles = useMemo(() => {
    const lives = key.split(',').filter(Boolean).map(id => services.people.account(id));
    let previous = lives.map(live => live.get()); let accounts = previous.filter((account): account is Account => !!account);
    return {
      get: () => {
        const next = lives.map(live => live.get());
        if (next.some((account, index) => account !== previous[index])) { previous = next; accounts = next.filter((account): account is Account => !!account); }
        return accounts;
      },
      subscribe: (listener: () => void) => { const stops = lives.map(live => live.subscribe(listener)); return () => stops.forEach(stop => stop()); },
    };
  }, [key, services]);
  return useLive(profiles);
}
export { acknowledgedCapabilities } from './capabilities';
export type { SupportIncident, SupportIntent, SupportUpdate } from './support';
export { decodeSupportUpdate, decodeSupportIncident, decodeSupportIntent } from './support';
export { decodeSupportPush, supportPushText, type SupportPush } from './support-push';
export function useAcknowledgedCapabilities(): readonly string[] { return useLive(acknowledgedCapabilities(useServices().http)); }
/** Sync's update callback runs only after the cursor transaction commits. No Support state is persisted. */
export function useSupportUpdates(handler: (event: SupportUpdate) => void): void {
  const services = useServices(); const latest = useRef(handler); latest.current = handler;
  useEffect(() => services.sync.onUpdate(['support.incident.changed', 'support.intent.changed'], update => { const event = decodeSupportUpdate(update); if (event) latest.current(event); }), [services]);
}
export { documentHistoryFor, documentJournalFor, documentResourcesFor, imageGalleryFor } from './document-host';
export { resolveContentReference, validContentPath, documentResourceInfo } from './document-resources';
export type { DocumentHostIdentity, DocumentResourceInfo, DocumentResources, ContentReference, DocumentDownloadOptions } from './document-resources';
export { createDocumentHistory, selectDocumentHistory } from './document-history';
export type { DocumentHistory, DocumentHistoryChain, DocumentHistoryEntry, DocumentHistoryVersion, DocumentRestore } from './document-history';
export type { DocumentJournal, DocumentLocation, DocumentLease, DocumentDraftPayload, DocumentCandidate } from './document-journal';
export type { ImageGallery, ImageGalleryState, GalleryImage, GallerySelection } from './gallery';
export type { ContentCore, ContentTree, ElementOp, ContentAdmission, ContentPrefixAdmission } from './content';
export type { ContentEditorHost, ContentDocument, ContentPrecondition, ContentCounter, ContentSaveOutcome, ContentResourcePick } from './content/host';
export async function loadContentCore() { return (await import('./content')).contentCore; }
export { contentText, contentCopy, type ContentCopyKey } from './content/copy';
export function useDocumentJournal() { const services = useServices(); useLive(services.session.lifetime); return documentJournalFor(services); }
export function useImageGallery(conversationId: Id) {
  const services = useServices(), lifetime = useLive(services.session.lifetime);
  const gallery = useMemo(() => imageGalleryFor(services, conversationId, lifetime), [services, lifetime, conversationId]);
  useEffect(() => { gallery?.start(); return () => gallery?.stop(); }, [gallery]); return gallery;
}
export function useDocumentResources(host: DocumentHostIdentity) {
  const services = useServices(), lifetime = useLive(services.session.lifetime);
  const identity = useMemo(() => ({ accountId: host.accountId, realmId: host.realmId, projectId: host.projectId, documentPath: host.documentPath, accessRevision: host.accessRevision, filesMode: host.filesMode }), [host.accountId, host.realmId, host.projectId, host.documentPath, host.accessRevision, host.filesMode]);
  const resources = useMemo(() => documentResourcesFor(services, identity, lifetime), [services, lifetime, identity]);
  useEffect(() => { resources.start(); return () => resources.stop(); }, [resources]); return resources;
}

/** The shell supplies a setter per sheet; features report only their own pending mutation. */
export const PresentationBusyContext = createContext<((busy: boolean) => void) | null>(null);
export function usePresentationBusy(busy: boolean) {
  const setBusy = useContext(PresentationBusyContext);
  useEffect(() => { setBusy?.(busy); return () => setBusy?.(false); }, [busy, setBusy]);
}

/** Call the returned function with the seqs actually visible in the active, unobscured transcript.
 * It never reads across an unseen incoming message or a missing history range. Explicit Mark as Read
 * remains services.messages.markRead(conversationId, conversation.last_seq).
 */
export function useVisibleReadTracker(conversationId: Id): (seqs: readonly number[]) => void {
  const services = useServices();
  const timeline = useTimeline(conversationId); const conversation = useConversation(conversationId);
  const visibleSeqs = useRef<readonly number[]>([]);
  const evaluate = useCallback((seqs?: readonly number[]) => {
    if (seqs !== undefined) visibleSeqs.current = seqs;
    if (document.visibilityState !== 'visible' || !document.hasFocus()) return;
    void services.messages.reportSeen(conversationId, visibleSeqs.current).catch(() => undefined);
  }, [conversationId, services]);
  useEffect(() => { evaluate(); }, [evaluate, timeline, conversation]);
  useEffect(() => {
    visibleSeqs.current = [];
    const wake = () => { if (document.visibilityState === 'visible' && document.hasFocus()) evaluate(); else void services.messages.flushSeen(conversationId).catch(() => undefined); };
    const flush = () => { void services.messages.flushSeen(conversationId).catch(() => undefined); };
    window.addEventListener('focus', wake); window.addEventListener('blur', flush); document.addEventListener('visibilitychange', wake);
    return () => { window.removeEventListener('focus', wake); window.removeEventListener('blur', flush); document.removeEventListener('visibilitychange', wake); flush(); };
  }, [evaluate, services, conversationId]);
  return useCallback((seqs: readonly number[]) => evaluate(seqs), [evaluate]);
}


export type { AudioOwner, AudioLease, AudioFocusService } from './audio-focus';
export type { WebPushController, WebPushState, WebPushHost, PushIntent, PushIdentity, PushFailure } from './push-host';
export interface WebClientInfo { version: string; build: string; supportUrl?: string }
const clientInfo: WebClientInfo = Object.freeze({ version: webVersion.name, build: webVersion.build });
/** No documented support URL is supplied by the current server config. */
export function useClientInfo(): WebClientInfo { return clientInfo; }
export function useAudioFocus(): AudioFocusService { const services = useServices(); useLive(services.session.lifetime); return audioFocusFor(services); }
export function useWebPush(): WebPushController | undefined { return useLive(webPushControllerLive(useServices())); }
export function useWebPushState(): WebPushState { const controller = useWebPush(); return useLive(controller?.state ?? unavailablePushState); }
export { accountDisplayName, conversationTitle, conversationMemberNames, conversationSearchNames } from './conversation-names';
